import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getPlatformStats } from '../utilities/adminUtils';
import { isUserAdmin, getCurrentUserUID } from '../../utilities/authUtils';
import { useAuth } from '../../context/AuthContext';
import PageContainer from '../../../PageUtil/PageContainer';

const AdminDashboard = () => {
  const { isAdmin, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      setCurrentUserId(user.uid);
    }
  }, [user]);

  useEffect(() => {
    if (isAdmin) {
      loadAdminData();
    }
  }, [isAdmin]);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      const platformStats = await getPlatformStats();
      setStats(platformStats);
    } catch (err) {
      console.error('Error loading admin data:', err);
      setError('Failed to load administrative data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageContainer error={error} setError={setError}>
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </PageContainer>
    );
  }

  if (!isAdmin && error) {
    return (
      <PageContainer error={error} setError={setError}>
        <div className="alert alert-danger mt-5">
          <h4>Access Denied</h4>
          <p>{error}</p>
          <button className="btn btn-primary" onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer error={error} setError={setError} isAdmin={isAdmin}>
      <div className="py-4">
        <h1 className="mb-4">Admin Dashboard</h1>

        {/* Stats Section */}
        {stats && (
          <div className="row g-3 mb-5">
            <div className="col-md-4">
              <div className="card text-white bg-primary h-100 shadow-sm">
                <div className="card-body text-center">
                  <h3 className="display-4">{stats.userCount}</h3>
                  <p className="card-text">Total Users</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-white bg-success h-100 shadow-sm">
                <div className="card-body text-center">
                  <h3 className="display-4">{stats.totalPublishedPortfolios}</h3>
                  <p className="card-text">User Portfolios</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card text-white bg-info h-100 shadow-sm">
                <div className="card-body text-center">
                  <h3 className="display-4">{stats.sharedCount}</h3>
                  <p className="card-text">Shared Portfolios</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* System Diagnostics Section */}
        <div className="mb-5">
          <h3 className="h4 mb-3 fw-bold">System Diagnostics</h3>
          <div className="row g-3">
            <div className="col-sm-6 col-lg-4">
              <button
                className="btn btn-dark w-100 py-3 rounded-2"
                onClick={() => navigate(`/debug/images/${currentUserId}`)}
              >
                <i className="bi bi-bug me-2 fs-5"></i>
                <div>Debug Image Visibility</div>
              </button>
            </div>
            <div className="col-sm-6 col-lg-4">
              <button
                className="btn btn-dark w-100 py-3 rounded-2"
                onClick={() => navigate(`/debug/projects/${currentUserId}`)}
              >
                <i className="bi bi-bug me-2 fs-5"></i>
                <div>Debug Project Visibility</div>
              </button>
            </div>
            <div className="col-sm-6 col-lg-4">
              <button
                className="btn btn-dark w-100 py-3 rounded-2"
                onClick={() => navigate(`/debug/skills/${currentUserId}`)}
              >
                <i className="bi bi-bug me-2 fs-5"></i>
                <div>Debug Skill Visibility</div>
              </button>
            </div>
          </div>
        </div>

        {/* Admin Actions Section */}
        <div className="mb-5">
          <h3 className="h4 mb-3 fw-bold">Admin Actions</h3>
          <div className="row g-3">
            <div className="col-sm-6 col-lg-4">
              <button
                className="btn btn-primary w-100 py-3 rounded-2"
                onClick={() => navigate('/admin/users')}
              >
                <i className="bi bi-people me-2 fs-5"></i>
                <div>Manage Users</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </PageContainer>
  );
};

export default AdminDashboard;

